/**
 * Unsent form input kept in the browser, so an accidental tab close, reload or
 * back gesture does not throw it away.
 *
 * The drafts hold personal data — a child's name, date of birth, diagnosis — so
 * they are kept on a short leash: one key per user (colleagues sharing a machine
 * never see each other's), removed on logout, and dropped after seven days.
 *
 * Storage can be missing or throw (private window, blocked site data). Every
 * function here swallows that: without storage there simply is no draft.
 */

const PREFIX = 'juniva:draft:';

export const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface FormDraft<T> {
  values: T;
  /** ISO timestamp of the last write. */
  savedAt: string;
}

interface Options {
  storage?: Storage | null;
  now?: Date;
}

function defaultStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function draftKey(form: string, userId: string): string {
  return `${PREFIX}${form}:${userId}`;
}

export function readDraft<T>(
  key: string,
  { storage = defaultStorage(), now = new Date() }: Options = {},
): FormDraft<T> | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    if (raw === null) return null;

    const parsed = JSON.parse(raw) as Partial<FormDraft<T>>;
    const savedAt = typeof parsed?.savedAt === 'string' ? Date.parse(parsed.savedAt) : NaN;
    const valid = parsed && typeof parsed.values === 'object' && parsed.values !== null;

    if (!valid || Number.isNaN(savedAt) || now.getTime() - savedAt > DRAFT_MAX_AGE_MS) {
      storage.removeItem(key);
      return null;
    }
    return { values: parsed.values as T, savedAt: parsed.savedAt as string };
  } catch {
    // Corrupt JSON lands here too; clearing it keeps it from failing every visit.
    try {
      storage.removeItem(key);
    } catch {
      // storage refuses even that — nothing left to do
    }
    return null;
  }
}

export function writeDraft<T>(
  key: string,
  values: T,
  { storage = defaultStorage(), now = new Date() }: Options = {},
): void {
  if (!storage) return;
  try {
    const draft: FormDraft<T> = { values, savedAt: now.toISOString() };
    storage.setItem(key, JSON.stringify(draft));
  } catch {
    // Quota exceeded or blocked: the form keeps working, it just is not backed up.
  }
}

export function removeDraft(key: string, storage: Storage | null = defaultStorage()): void {
  try {
    storage?.removeItem(key);
  } catch {
    // see writeDraft
  }
}

const emptyToNull = (value: unknown) => (value === '' || value === undefined ? null : value);

/**
 * Whether two flat form value objects say the same thing. `''`, `null` and
 * `undefined` all mean "empty" — a form hands back whichever it likes — and key
 * order does not matter. Used to tell an untouched form from a started one.
 */
export function sameFormValues(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...keys].every((key) => emptyToNull(a[key]) === emptyToNull(b[key]));
}

/** On logout: every draft of every form, nothing else in storage. */
export function removeAllDrafts(storage: Storage | null = defaultStorage()): void {
  if (!storage) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(PREFIX)) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch {
    // see writeDraft
  }
}
